import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send, Upload, X } from "lucide-react";
import { toast, Toaster } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSettings } from "@/context/AppContext";
import PublicLayout from "@/components/PublicLayout";
import { compressMany } from "@/lib/imageUtils";

const TYPES = ["Création de jardin", "Entretien", "Taille de haies", "Pose de gazon", "Plantations", "Aménagements", "Terrasse", "Nettoyage", "Autre"];

const fileToB64 = (file) =>
  new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(fr.result);
    fr.onerror = rej;
    fr.readAsDataURL(file);
  });

export default function ContactPage() {
  const { settings } = useSettings();
  const [form, setForm] = useState({
    nom: "", prenom: "", telephone: "", email: "", adresse_chantier: "",
    type_demande: "", message: "", photos: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6 - form.photos.length);
    const { results, errors } = await compressMany(files);
    if (results.length) update("photos", [...form.photos, ...results].slice(0, 6));
    errors.forEach((er) => toast.error(`${er.name} : ${er.message}`));
    e.target.value = "";
  };

  const removePhoto = (i) =>
    update("photos", form.photos.filter((_, k) => k !== i));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Demande envoyée ! Nous vous répondrons rapidement.");
      setSuccess(true);
      setForm({ nom: "", prenom: "", telephone: "", email: "", adresse_chantier: "", type_demande: "", message: "", photos: [] });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <Toaster richColors position="top-center" />
      <section className="pt-36 pb-12 container-px max-w-7xl mx-auto">
        <p className="label-eyebrow mb-5">Contact</p>
        <h1 className="font-display text-5xl md:text-7xl text-forest font-light tracking-tight max-w-3xl leading-[0.95]">
          Demandez votre devis.
        </h1>
        <p className="mt-8 max-w-xl text-forest-soft">
          Décrivez-nous votre projet — nous revenons vers vous sous 48h ouvrées.
        </p>
      </section>

      <section className="container-px max-w-7xl mx-auto pb-32 grid grid-cols-1 md:grid-cols-12 gap-10">
        <aside className="md:col-span-4 space-y-7">
          {settings?.contact_phone && (
            <div className="border-t border-sand pt-5">
              <p className="label-eyebrow mb-2">Téléphone</p>
              <a href={`tel:${settings.contact_phone.replace(/\s/g, "")}`} className="flex items-center gap-3 text-forest hover:text-corten">
                <Phone className="w-4 h-4" /> {settings.contact_phone}
              </a>
            </div>
          )}
          {settings?.contact_email && (
            <div className="border-t border-sand pt-5">
              <p className="label-eyebrow mb-2">Email</p>
              <a href={`mailto:${settings.contact_email}`} className="flex items-center gap-3 text-forest hover:text-corten break-all">
                <Mail className="w-4 h-4 shrink-0" /> {settings.contact_email}
              </a>
            </div>
          )}
          {settings?.contact_address && (
            <div className="border-t border-sand pt-5">
              <p className="label-eyebrow mb-2">Adresse</p>
              <p className="flex items-start gap-3 text-forest"><MapPin className="w-4 h-4 mt-1" /> {settings.contact_address}</p>
            </div>
          )}
          {settings?.contact_hours && (
            <div className="border-t border-sand pt-5">
              <p className="label-eyebrow mb-2">Horaires</p>
              <p className="flex items-start gap-3 text-forest text-sm whitespace-pre-line"><Clock className="w-4 h-4 mt-1 shrink-0" /> {settings.contact_hours}</p>
            </div>
          )}
        </aside>

        <form onSubmit={submit} className="md:col-span-8 bg-cream border border-sand p-8 md:p-12 space-y-6" data-testid="contact-form">
          {success && (
            <div className="bg-forest/5 border border-forest/20 text-forest p-4 text-sm">
              Merci ! Votre demande a bien été enregistrée.
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Prénom *" value={form.prenom} onChange={(v) => update("prenom", v)} required testid="field-prenom" />
            <Field label="Nom *" value={form.nom} onChange={(v) => update("nom", v)} required testid="field-nom" />
            <Field label="Téléphone *" value={form.telephone} onChange={(v) => update("telephone", v)} required testid="field-telephone" />
            <Field label="Email *" type="email" value={form.email} onChange={(v) => update("email", v)} required testid="field-email" />
            <Field label="Adresse du chantier" value={form.adresse_chantier} onChange={(v) => update("adresse_chantier", v)} testid="field-adresse" className="sm:col-span-2" />
          </div>

          <div>
            <label className="label-eyebrow block mb-2">Type de demande</label>
            <select
              data-testid="field-type"
              value={form.type_demande}
              onChange={(e) => update("type_demande", e.target.value)}
              className="w-full border-b border-sand bg-transparent py-3 text-forest focus:border-forest focus:outline-none transition"
            >
              <option value="">Choisir...</option>
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div>
            <label className="label-eyebrow block mb-2">Message *</label>
            <textarea
              required
              data-testid="field-message"
              rows={6}
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              className="w-full border border-sand bg-transparent p-4 text-forest focus:border-forest focus:outline-none transition resize-none"
              placeholder="Décrivez votre projet, la surface approximative, vos envies..."
            />
          </div>

          <div>
            <label className="label-eyebrow block mb-2">Photos (optionnel)</label>
            <div className="flex flex-wrap gap-3 items-center">
              {form.photos.map((p, i) => (
                <div key={i} className="relative w-20 h-20">
                  <img src={p} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePhoto(i)}
                    className="absolute -top-2 -right-2 bg-forest text-cream w-6 h-6 rounded-full flex items-center justify-center"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {form.photos.length < 6 && (
                <label className="w-20 h-20 border border-dashed border-sand flex items-center justify-center cursor-pointer hover:border-forest transition" data-testid="field-photos">
                  <Upload className="w-5 h-5 text-forest-soft" />
                  <input type="file" multiple accept="image/*" className="hidden" onChange={onFiles} />
                </label>
              )}
            </div>
            <p className="text-xs text-forest-soft mt-2">Jusqu'à 6 images. Format JPG/PNG.</p>
          </div>

          <button type="submit" disabled={submitting} className="btn-primary w-full sm:w-auto" data-testid="submit-contact">
            {submitting ? "Envoi..." : "Envoyer la demande"} <Send className="w-4 h-4" />
          </button>
        </form>
      </section>
    </PublicLayout>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, className = "" }) {
  return (
    <div className={className}>
      <label className="label-eyebrow block mb-2">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border-b border-sand bg-transparent py-3 text-forest focus:border-forest focus:outline-none transition"
      />
    </div>
  );
}
